import React, { useState } from 'react';
import { Banknote, Smartphone, Edit2, Save, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const payoutFrequencies = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const PayoutSettings = ({ profile, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    mpesaNumber: profile?.mpesaNumber || '',
    bankName: profile?.bankName || '',
    bankAccount: profile?.bankAccount || '',
    payoutFrequency: profile?.payoutFrequency || 'weekly',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { error: dbError } = await supabase?.from('driver_profiles')?.update({
        mpesa_number: formData?.mpesaNumber,
        bank_name: formData?.bankName,
        bank_account: formData?.bankAccount,
        payout_frequency: formData?.payoutFrequency,
      })?.eq('user_id', user?.id);
      if (dbError) throw dbError;
      await onUpdate?.(formData);
      setEditing(false);
      setSuccess('Payout settings updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Banknote className="w-4 h-4 text-[#1a365d]" />
          <h3 className="font-semibold text-[#1a365d]">Banking & Payout</h3>
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium hover:bg-slate-200 transition-colors">
            <Edit2 className="w-3 h-3" /> Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a365d] text-white rounded-lg text-xs font-medium hover:bg-[#2d5a87] transition-colors disabled:opacity-50">
              <Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setError(''); }} className="p-1.5 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      <div className="p-5 space-y-4">
        {(error || success) && (
          <div className={`px-4 py-2.5 rounded-lg text-sm font-medium ${error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
            {error || success}
          </div>
        )}

        {/* M-Pesa */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-4 h-4 text-green-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 mb-0.5">M-Pesa Number</p>
            {editing ? (
              <input
                type="tel"
                value={formData?.mpesaNumber}
                onChange={e => setFormData(p => ({ ...p, mpesaNumber: e?.target?.value }))}
                placeholder="+254 7XX XXX XXX"
                className="w-full text-sm border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/30 focus:border-[#1a365d]"
              />
            ) : (
              <p className="text-sm font-semibold text-slate-800">{formData?.mpesaNumber || '—'}</p>
            )}
          </div>
        </div>

        {/* Bank */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Banknote className="w-4 h-4 text-blue-700" />
          </div>
          <div className="flex-1 min-w-0 grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Bank Name</p>
              {editing ? (
                <input
                  type="text"
                  value={formData?.bankName}
                  onChange={e => setFormData(p => ({ ...p, bankName: e?.target?.value }))}
                  placeholder="e.g. Equity Bank"
                  className="w-full text-sm border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/30 focus:border-[#1a365d]"
                />
              ) : (
                <p className="text-sm font-semibold text-slate-800">{formData?.bankName || '—'}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Account Number</p>
              {editing ? (
                <input
                  type="text"
                  value={formData?.bankAccount}
                  onChange={e => setFormData(p => ({ ...p, bankAccount: e?.target?.value }))}
                  placeholder="Account number"
                  className="w-full text-sm border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/30 focus:border-[#1a365d]"
                />
              ) : (
                <p className="text-sm font-semibold text-slate-800">{formData?.bankAccount ? `•••• ${formData?.bankAccount?.slice(-4)}` : '—'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Payout Frequency */}
        <div>
          <p className="text-xs text-slate-500 mb-2">Payout Frequency</p>
          <div className="flex gap-2">
            {payoutFrequencies?.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => editing && setFormData(p => ({ ...p, payoutFrequency: value }))}
                disabled={!editing}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${formData?.payoutFrequency === value ? 'bg-[#1a365d] text-white' : 'bg-slate-100 text-slate-600'} ${editing ? 'hover:bg-[#2d5a87] hover:text-white cursor-pointer' : 'cursor-default'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayoutSettings;
